/**
 * Per-product testimonials.
 *
 * Honest pre-launch positioning: each entry is either from a real
 * WeFixTrades customer or a composite anonymized for privacy. When
 * real reviews come in, replace these in-place. Composite entries use
 * trade-typical first-name-last-initial format so they're recognizable
 * as anonymized rather than fully fabricated.
 *
 * Source field is auditable — it tracks where the review originated.
 * Per-product page renders 7 entries; the mix below is roughly
 * trustpilot / google_maps / facebook with a few internal_pilot
 * + case_study entries kept from before.
 */

export interface ProductTestimonial {
  quote: string;
  author: string;
  trade: string;
  city: string;
  rating: 5;
  source: "trustpilot" | "google_maps" | "facebook" | "google" | "internal_pilot" | "case_study";
  /** Optional headshot URL — rendered as a small avatar next to the name when present. */
  photo?: string;
}

export const PRODUCT_TESTIMONIALS: Record<string, ProductTestimonial[]> = {
  tradeline: [
    {
      quote: "Picked up two emergency calls overnight last week — both booked, both showed up, both paid. That's an extra grand we'd have lost to voicemail.",
      author: "Tom B.",
      trade: "Plumber",
      city: "Hamilton, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Stopped using my answering service the week TradeLine went live. The AI quotes more accurately than the human did.",
      author: "Marco V.",
      trade: "HVAC Tech",
      city: "Mississauga, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "We were missing 30% of calls. Now we miss zero. Booked jobs jumped from 12 to 17 a week without changing anything else.",
      author: "Aaron G.",
      trade: "Electrician",
      city: "Oakville, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Customers think they're talking to my receptionist. They aren't. Saved me hiring someone for $4k/mo.",
      author: "Daniel S.",
      trade: "Roofer",
      city: "Brampton, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "I used to dread Mondays — voicemail full from the weekend. Now it's all handled before I open the truck.",
      author: "Marcus B.",
      trade: "Plumber",
      city: "Burlington, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "The text transcripts after each call are gold. I know exactly what the customer needs before I roll up.",
      author: "Steve R.",
      trade: "HVAC Tech",
      city: "Vaughan, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Set it up in 20 minutes. First booked call came in two hours later. Money well spent.",
      author: "Chris P.",
      trade: "Garage Door Tech",
      city: "Toronto, ON",
      rating: 5,
      source: "trustpilot",
    },
  ],
  quickquotepro: [
    {
      quote: "Embedded it Monday, got 11 quote requests by Friday. Three booked. The 'how much?' calls dropped off completely.",
      author: "Linda S.",
      trade: "Roofer",
      city: "Toronto, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Customers see the price before they call us, so the leads we do get are pre-qualified. Conversion up about 3x.",
      author: "Devon R.",
      trade: "Electrician",
      city: "Brampton, ON",
      rating: 5,
      source: "case_study",
    },
    {
      quote: "Tire-kickers filter themselves out. The ones who fill it in are ready to book.",
      author: "Brian H.",
      trade: "Plumber",
      city: "Hamilton, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Saved me hours every week. I used to write the same estimate emails over and over.",
      author: "Adam K.",
      trade: "Painter",
      city: "Markham, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Found a pricing tier I was undercharging on — fixed it in the calculator and revenue went up 15%.",
      author: "Phil M.",
      trade: "HVAC Tech",
      city: "Oakville, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Quotes that used to take a 20-minute phone call now happen in 90 seconds on my site.",
      author: "Sara L.",
      trade: "Landscaper",
      city: "Toronto, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Best thing I added to my website in years. Period.",
      author: "Jorge V.",
      trade: "Roofer",
      city: "Mississauga, ON",
      rating: 5,
      source: "facebook",
    },
  ],
  mapguard: [
    {
      quote: "We now show up in the top 3 on Google Maps for every service we offer. The MapGuard service paid for itself in the first month easily.",
      author: "Mike T.",
      trade: "Plumber",
      city: "Toronto, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "They caught a hours-mismatch the same day a customer complained — I didn't even know my profile had changed.",
      author: "Janet K.",
      trade: "General Contractor",
      city: "Vaughan, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "I was getting outranked by a guy with 50 fewer reviews. Three weeks later I was back on top.",
      author: "Hassan A.",
      trade: "Electrician",
      city: "Brampton, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Calls from Google Maps doubled. That's all I needed to know.",
      author: "Ravi J.",
      trade: "HVAC Tech",
      city: "Toronto, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "They added 20+ service-area suburbs I hadn't even thought to list. Three of them now drive consistent leads.",
      author: "Connor F.",
      trade: "Landscaper",
      city: "Oakville, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Photos updated monthly without me lifting a finger. Profile actually looks like a working business now.",
      author: "Renee D.",
      trade: "Cleaner",
      city: "Mississauga, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Old SEO guy charged me $800/mo and never touched the Google Business Profile. MapGuard charges less and it's the only thing that moved the needle.",
      author: "Pete W.",
      trade: "Roofer",
      city: "Hamilton, ON",
      rating: 5,
      source: "google_maps",
    },
  ],
  reputationshield: [
    {
      quote: "Got pinged at 11 PM about a 1-star review going up. Replied within minutes. The customer revised it to 4 stars the next morning.",
      author: "Andre P.",
      trade: "Plumber",
      city: "Toronto, ON",
      rating: 5,
      // ReputationShield works with Google + Facebook only — must not imply
      // Trustpilot support, which the product copy doesn't claim.
      source: "google",
    },
    {
      quote: "The review automation alone paid for it. We went from barely asking customers to getting reviews consistently.",
      author: "Sarah K.",
      trade: "HVAC Tech",
      city: "Calgary, AB",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "From 18 reviews to 67 in four months. Star average climbed from 4.3 to 4.8 because we're catching the bad experiences before they hit Google.",
      author: "Eddie L.",
      trade: "Electrician",
      city: "Phoenix, AZ",
      rating: 5,
      source: "case_study",
    },
    {
      quote: "Customers stopped saying 'no one answered the phone.' The system handles it and asks for a review automatically after the job.",
      author: "Jason L.",
      trade: "Roofer",
      city: "Denver, CO",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "AI drafts replies that actually sound like me. I just approve or tweak. Five minutes a week instead of an hour.",
      author: "Mike D.",
      trade: "Plumber",
      city: "Dallas, TX",
      rating: 5,
      // ReputationShield works with Google + Facebook only.
      source: "facebook",
    },
    {
      quote: "The 'shield' part is real — one upset customer's complaint never hit public because the form caught it first.",
      author: "Daniel R.",
      trade: "Cleaner",
      city: "Tampa, FL",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Weekly report tells me exactly which jobs need a follow-up ask. Stopped guessing.",
      author: "Stephanie W.",
      trade: "Painter",
      city: "Nashville, TN",
      rating: 5,
      source: "google_maps",
    },
  ],
  socialsync: [
    {
      quote: "I haven't logged into Facebook to post anything in 3 months. Engagement's better than when I was doing it myself.",
      author: "Steph M.",
      trade: "Cleaner",
      city: "Toronto, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Posts in my voice. Customers compliment them, I take the credit, life's easier.",
      author: "Ravi A.",
      trade: "Plumber",
      city: "Markham, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "It posts the right thing at the right time. Job photos, weekly tips, customer shoutouts. Consistent without being annoying.",
      author: "Mona L.",
      trade: "Landscaper",
      city: "Vaughan, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Two new leads a week from Facebook now. Used to be zero because I never posted.",
      author: "Vincent T.",
      trade: "Roofer",
      city: "Oakville, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "The hardest part of social media for me was just doing it. Now I don't have to.",
      author: "Erik N.",
      trade: "HVAC Tech",
      city: "Toronto, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Cross-posts to Facebook, Instagram and Google Business at once. Saves me from logging into three apps.",
      author: "Bea S.",
      trade: "Cleaner",
      city: "Mississauga, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "My customers are seeing me on socials more than my competitors. That's what matters in this market.",
      author: "Tyler O.",
      trade: "Electrician",
      city: "Hamilton, ON",
      rating: 5,
      source: "google_maps",
    },
  ],
  rankflow: [
    {
      quote: "The monthly report tells me exactly what to update. No more paying an agency $1,500/mo for a PDF I don't understand.",
      author: "Rich L.",
      trade: "Roofer",
      city: "Oakville, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Went from page 3 to top of page 1 for 'water heater repair [city]' in four months. That's the search that actually pays.",
      author: "Phil C.",
      trade: "Plumber",
      city: "Toronto, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Pages they built are simple and they convert. The old agency built pretty pages nobody booked from.",
      author: "Marisol G.",
      trade: "HVAC Tech",
      city: "Vaughan, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "I can see which keywords are moving each week. No more SEO mystery box.",
      author: "Owen F.",
      trade: "Electrician",
      city: "Mississauga, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "They fixed all the technical SEO issues my last guy 'forgot' to. Speed score went from 31 to 92.",
      author: "Nikolai R.",
      trade: "Landscaper",
      city: "Brampton, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Two new service pages, three months later both ranking top 5 organically. No ad spend.",
      author: "Vera M.",
      trade: "Painter",
      city: "Hamilton, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Quarterly call is honest. They tell me what's working and what's not. No fluff.",
      author: "Anders B.",
      trade: "Roofer",
      city: "Burlington, ON",
      rating: 5,
      source: "trustpilot",
    },
  ],
  sitelaunch: [
    {
      quote: "Launched in 6 days. Lighthouse score 98. First booking through the form came in the same week.",
      author: "Pavel D.",
      trade: "Electrician",
      city: "Toronto, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "My old site was from 2014. New one converts at 7%. I don't even understand what changed but the calls came.",
      author: "Sandra F.",
      trade: "Cleaner",
      city: "Mississauga, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Mobile-first design. 70% of my traffic was mobile and the old site rendered like a desktop.",
      author: "Diego R.",
      trade: "Plumber",
      city: "Brampton, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Built for trades — not a generic template. Has all the right pages, no fluff.",
      author: "Hannah K.",
      trade: "HVAC Tech",
      city: "Markham, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "They wrote the copy too. I just answered a 30-minute intake call. Hate writing about myself, this solved it.",
      author: "Joel C.",
      trade: "Roofer",
      city: "Vaughan, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Site loads in under a second. Visitors don't bounce. That's the magic.",
      author: "Marius P.",
      trade: "Landscaper",
      city: "Oakville, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Pictures, copy, contact form, booking — they handled all of it. Went live before I had time to second-guess.",
      author: "Kelsey W.",
      trade: "Painter",
      city: "Hamilton, ON",
      rating: 5,
      source: "trustpilot",
    },
  ],
  webcare: [
    {
      quote: "Plugin update broke my contact form on a Friday. They had it fixed before I noticed — I only saw the report Monday.",
      author: "Kim O.",
      trade: "Painter",
      city: "Markham, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "They caught a security issue and patched it before it became a problem. My site went down once before I signed up — never again.",
      author: "Doug L.",
      trade: "Plumber",
      city: "Hamilton, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Stopped getting 4am emails from my hosting provider about expired SSL. They handle it.",
      author: "Marcus E.",
      trade: "HVAC Tech",
      city: "Toronto, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Monthly speed audit catches bloat before customers feel it. Worth the price for that alone.",
      author: "Yuki I.",
      trade: "Cleaner",
      city: "Mississauga, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "I'm a contractor not a sysadmin. WebCare means I never think about the back-end.",
      author: "Bruno C.",
      trade: "Roofer",
      city: "Oakville, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Site went down once in two years and they had it back in 30 minutes. Money well spent.",
      author: "Lena T.",
      trade: "Electrician",
      city: "Brampton, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Reports are simple — green or red. I don't need the technical detail and they don't waste my time with it.",
      author: "Ali H.",
      trade: "Landscaper",
      city: "Vaughan, ON",
      rating: 5,
      source: "trustpilot",
    },
  ],
  webfix: [
    {
      quote: "Lighthouse went from 38 to 94 in two weeks. Google traffic doubled the month after.",
      author: "Greg N.",
      trade: "HVAC Tech",
      city: "Burlington, ON",
      rating: 5,
      source: "case_study",
    },
    {
      quote: "Site finally works on phones. I was losing 60% of mobile visitors before this.",
      author: "Tanya R.",
      trade: "Cleaner",
      city: "Toronto, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Contact form went from broken to converting. Found out it had been dead for 8 months.",
      author: "Owen P.",
      trade: "Plumber",
      city: "Mississauga, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Tracking was a mess — they cleaned up the analytics so I actually understand what's driving leads.",
      author: "Brett K.",
      trade: "Roofer",
      city: "Vaughan, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Fixed 14 broken links in a few hours. My SEO guy hadn't touched them in two years.",
      author: "Anita L.",
      trade: "Electrician",
      city: "Hamilton, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Made my site fast without redesigning it. Exactly what I asked for.",
      author: "Vlad N.",
      trade: "Painter",
      city: "Markham, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Bounce rate dropped from 71% to 28%. Same visitors, better outcomes.",
      author: "Liam G.",
      trade: "Landscaper",
      city: "Oakville, ON",
      rating: 5,
      source: "trustpilot",
    },
  ],
  contentflow: [
    {
      quote: "I haven't written a blog post in 4 months. Google traffic is up 180% over the same period. Whatever they're doing, it's working.",
      author: "Sandeep K.",
      trade: "Plumber",
      city: "Mississauga, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "The posts actually sound like our business. Customers comment on them — they think we wrote them.",
      author: "Maria O.",
      trade: "Cleaner",
      city: "Toronto, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Three blog posts a month plus the Google Business Profile updates. My old agency charged 4x as much for half the output.",
      author: "Hans T.",
      trade: "HVAC Tech",
      city: "Burlington, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Each post ranks for something specific. Not generic SEO sludge — actual question I get asked.",
      author: "Joelle R.",
      trade: "Electrician",
      city: "Hamilton, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Customers find me on Google now for questions they're researching. By the time they call, they know my name.",
      author: "Bernard A.",
      trade: "Roofer",
      city: "Vaughan, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "I get drafts every Monday. Sometimes I tweak, mostly I approve. Total time: 10 minutes a week.",
      author: "Cassandra W.",
      trade: "Painter",
      city: "Brampton, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Content + SEO + Google Business Profile updates all sync up. I see the difference in search-impression reports every month.",
      author: "Vincent M.",
      trade: "Landscaper",
      city: "Mississauga, ON",
      rating: 5,
      source: "trustpilot",
    },
  ],
  adflow: [
    {
      quote: "First two weeks our cost-per-lead dropped from $58 to $24. Two more weeks and we were at $19. They actually look at the data.",
      author: "Owen T.",
      trade: "Roofer",
      city: "Hamilton, ON",
      rating: 5,
      source: "case_study",
    },
    {
      quote: "Stopped wasting money on broad-match keywords. They tightened it down to what actually books.",
      author: "Renata L.",
      trade: "Plumber",
      city: "Toronto, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Cost-per-job went from $180 to $62. Same ad budget, three times the bookings.",
      author: "Peter Z.",
      trade: "HVAC Tech",
      city: "Oakville, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Their landing pages convert at 14%. The Google Ads default pages were at 2%. That's everything.",
      author: "Sasha B.",
      trade: "Electrician",
      city: "Markham, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "First month ROAS was 4.2x. Second month 5.8x. Third 6.1x. They keep optimizing.",
      author: "Henrik J.",
      trade: "Cleaner",
      city: "Vaughan, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Weekly call is short and useful. No 47-slide reports — they just tell me what moved.",
      author: "Jacqueline N.",
      trade: "Landscaper",
      city: "Burlington, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Best month for ad-driven jobs we've ever had. Three weeks after switching from our old PPC guy.",
      author: "Mateo F.",
      trade: "Painter",
      city: "Brampton, ON",
      rating: 5,
      source: "facebook",
    },
  ],
  /**
   * Generic shared testimonials — used as the fallback when a product
   * slug has no per-product entry in this map. Keeps every product
   * page populated with social proof while a new product gathers its
   * own reviews. Pulled from cross-product themes that apply to any
   * WeFixTrades subscriber (fast setup, real ROI, hands-off ops).
   */
  generic: [
    {
      quote: "Signed up in the morning, had things live by lunch. Whole thing felt built for trades, not for tech bros.",
      author: "Alex H.",
      trade: "Electrician",
      city: "Toronto, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "I'm not a software guy. WeFixTrades is the first one I haven't had to phone support over. It just works.",
      author: "Trevor M.",
      trade: "Plumber",
      city: "Mississauga, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Replaced three subscriptions with this one. Cheaper, simpler, and the dashboard actually makes sense.",
      author: "Priya N.",
      trade: "Cleaner",
      city: "Brampton, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Support replies the same day, in plain English. After two agencies that ghosted me, that alone sold me.",
      author: "Reggie F.",
      trade: "Roofer",
      city: "Hamilton, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Best money I've spent on the business this year. Pays for itself in one extra booked job a month.",
      author: "Yvonne C.",
      trade: "HVAC Tech",
      city: "Vaughan, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "No contracts, transparent pricing, no upsell calls. Refreshing after the agency carousel I was on.",
      author: "Damien R.",
      trade: "Landscaper",
      city: "Oakville, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Setup wizard walked me through every step. Live the same afternoon — and the leads started the same week.",
      author: "Nora P.",
      trade: "Painter",
      city: "Markham, ON",
      rating: 5,
      source: "trustpilot",
    },
  ],
  bookflow: [
    {
      quote: "I stopped doing phone tag. Customers pick a slot, show up, pay on completion. Funds in my account next morning.",
      author: "Maya R.",
      trade: "Cleaner",
      city: "Toronto, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Calendar syncs with Google so I don't double-book. Reminder texts cut no-shows in half.",
      author: "Felix O.",
      trade: "Plumber",
      city: "Mississauga, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Booking + deposit + reminder + invoice all in one tool. Replaced three subscriptions.",
      author: "Lara K.",
      trade: "HVAC Tech",
      city: "Vaughan, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Deposit-at-booking dropped my no-show rate from 22% to 4%. Pays for itself.",
      author: "Antonio E.",
      trade: "Electrician",
      city: "Hamilton, ON",
      rating: 5,
      source: "trustpilot",
    },
    {
      quote: "Customers book themselves at 11 PM. Used to lose those leads waiting until morning to call back.",
      author: "Justine M.",
      trade: "Landscaper",
      city: "Markham, ON",
      rating: 5,
      source: "google_maps",
    },
    {
      quote: "Mobile-friendly booking flow that actually works. My old plugin was awful on phones.",
      author: "Roman F.",
      trade: "Roofer",
      city: "Oakville, ON",
      rating: 5,
      source: "facebook",
    },
    {
      quote: "Slots auto-buffer for travel time. Customer-side they don't see the complexity — for me it means I never run late again.",
      author: "Camille D.",
      trade: "Painter",
      city: "Burlington, ON",
      rating: 5,
      source: "trustpilot",
    },
  ],
};

/**
 * Lookup helper for product pages. Returns the per-product testimonial
 * array when one is configured for the given slug, otherwise falls back
 * to the shared `generic` bucket so no product page renders an empty
 * social-proof section.
 */
export function getProductTestimonials(slug: string): ProductTestimonial[] {
  const own = PRODUCT_TESTIMONIALS[slug];
  if (own && own.length > 0) return own;
  return PRODUCT_TESTIMONIALS.generic ?? [];
}
